import { useState, useCallback, useRef, useEffect } from "react";
import { GetSchedule } from "../GetSchedule";
import { oids } from "@/configs/Settings";

interface UseScheduleProps {
  /** Se false, fetchSchedules não executa chamada à API (para usar dados pré-carregados) */
  enabled?: boolean;
  /** ID do agendamento (opcional) */
  id?: string;
  /** ID do ator (profissional ou local) */
  actor?: string;
  /** ID do local */
  location?: string;
  /** Incluir slots */
  includeSlot?: boolean;
  /** Quantidade de registros */
  count?: number;
  /** Apenas agendamentos ativos */
  active?: boolean;
  /** Data inicial */
  startDate?: string;
  /** Especialidade médica */
  specialty?: string;
  /** Cidade */
  city?: string;
  /** Estado */
  state?: string;
  /** ID da organização */
  organization?: string;
  /** ID do profissional */
  practitioner?: string;
  /** Incluir informações do local */
  includeLocation?: boolean;
  /** Critérios de ordenação */
  sort?: string;
}

interface UseScheduleResult {
  /** Dados dos agendamentos */
  data: {
    schedules: fhir5.Schedule[];
    slots: fhir5.Slot[];
    total?: number;
  };
  /** Estado de carregamento */
  loading: boolean;
  /** Estado de erro */
  error: Error | null;
  /** Função para buscar os dados */
  fetchSchedules: () => Promise<void>;
}

/**
 * Extrai apenas os recursos do tipo Schedule do bundle
 * @param bundle Bundle retornado pela API
 * @returns Array de Schedule
 */
function extractSchedules(bundle?: fhir5.Bundle): fhir5.Schedule[] {
  if (!bundle?.entry) return [];

  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.Schedule> =>
        entry.resource?.resourceType === "Schedule"
    )
    .map((entry) => entry.resource!)
    .filter((resource): resource is fhir5.Schedule => resource !== undefined);
}

/**
 * Extrai apenas os recursos do tipo Slot do bundle
 * @param bundle Bundle retornado pela API
 * @returns Array de Slot
 */
function extractSlots(bundle?: fhir5.Bundle): fhir5.Slot[] {
  if (!bundle?.entry) return [];

  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.Slot> =>
        entry.resource?.resourceType === "Slot"
    )
    .map((entry) => entry.resource!)
    .filter((resource): resource is fhir5.Slot => resource !== undefined);
}

/**
 * Hook para buscar agendamentos
 *
 * @example
 * ```tsx
 * const { data, loading, error, fetchSchedules } = useSchedule({
 *   actor: \`Practitioner/\${oids.cpf}-\${user.username}\`,
 *   organization: \`\${oids.cnpj}-\${access.organizationId}\`,
 *   active: true
 * });
 *
 * // Buscar dados
 * useEffect(() => {
 *   fetchSchedules();
 * }, [fetchSchedules]);
 * ```
 */
export function useSchedule(props: UseScheduleProps): UseScheduleResult {
  const { enabled = true, ...scheduleProps } = props;
  const [data, setData] = useState<{
    schedules: fhir5.Schedule[];
    slots: fhir5.Slot[];
    total?: number;
  }>({
    schedules: [],
    slots: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Mantém uma referência estável dos parâmetros
  const paramsRef = useRef(scheduleProps);
  const enabledRef = useRef(enabled);

  // Atualiza a referência quando as props mudam
  useEffect(() => {
    paramsRef.current = scheduleProps;
    enabledRef.current = enabled;
  }, [scheduleProps, enabled]);

  const fetchSchedules = useCallback(async () => {
    // Quando enabled=false (ex: dados pré-carregados), não executa chamada
    if (!enabledRef.current) return;

    const currentParams = paramsRef.current;

    try {
      setLoading(true);
      setError(null);

      const response = await GetSchedule({
        id: currentParams.id,
        actor: currentParams.actor,
        location: currentParams.location,
        includeSlot: currentParams.includeSlot,
        count: currentParams.count,
        active: currentParams.active,
        startDate: currentParams.startDate,
        specialty: currentParams.specialty,
        city: currentParams.city,
        state: currentParams.state,
        organization: currentParams.organization,
        practitioner: currentParams.practitioner,
        includeLocation: currentParams.includeLocation,
        sort: currentParams.sort,
      });

      // Extrai Schedules e Slots do bundle
      const schedules = extractSchedules(response);
      const slots = extractSlots(response);

      setData({
        schedules,
        slots,
        total: response.total,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Erro ao buscar agendamentos")
      );
      console.error("useSchedule error:", err);
    } finally {
      setLoading(false);
    }
  }, []); // fetchSchedules não tem dependências externas

  return {
    data,
    loading,
    error,
    fetchSchedules,
  };
}
